import { useToast } from "../../hooks/useToast";
import { ToastContainer } from "../../components/common/Toast";
import { useState, useEffect, useCallback, memo } from "react";
import { useGrowthEquation } from "../../hooks/marketing/useGrowthEquation";
import { useProjections } from "../../hooks/marketing/useProjections";
import { useROASDistribution } from "../../hooks/marketing/useROASDistribution";
import { useMQLActivity } from "../../hooks/marketing/useMQLActivity";
import { useSocialMedia } from "../../hooks/marketing/useSocial";
import { useAEO } from "../../hooks/marketing/useAEO";
import { useEmail } from "../../hooks/marketing/useEmails";
import { useLinkedIn } from "../../hooks/marketing/useLinkedIn";
import { Header } from "../../components/marketing/Header"; // Reusing from sales
import { GrowthEquation } from "../../components/marketing/sections/GrowthEquation";
import { Projections } from "../../components/marketing/sections/Projections";
import { ROASDistribution } from "../../components/marketing/sections/ROASDistribution";
import { MQLActivity } from "../../components/marketing/sections/MQLActivity";
import { WebPerformance } from "../../components/marketing/sections/WebPerformance";
import { SocialMedia } from "../../components/marketing/sections/Social";
import { AEO } from "../../components/marketing/sections/AEO";
import { Email } from "../../components/marketing/sections/Email";
import { usePRDemo } from "../../hooks/marketing/usePRDemo";
import { PR } from "../../components/marketing/sections/PR";

import { LinkedInDeepdive } from "../../components/marketing/sections/LinkedIn";
import CollapsibleSection from "../../components/common/CollapsibleSection";



import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';


const MarketingDashboard = () => {
  const { toasts, removeToast, addToast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Default date range state
  const [dateRange, setDateRange] = useState(() => {
    const currentYear = new Date().getFullYear();
    return {
      start_date: `${currentYear}-01-01`,
      end_date: `${currentYear}-12-31`,
    };
  });

  // Growth Equation hook
  const {
    data: growthData,
    loading: loadingGrowth,
    refreshing: refreshingGrowth,
    error: growthError,
    refetch: refetchGrowth,
  } = useGrowthEquation(dateRange);

  // Projections hook
  const {
    inputs: projectionInputs,
    results: projectionResults,
    updateInput,
    resetInputs,
  } = useProjections();

  // ROAS Distribution hook
  const {
    data: roasData,
    loading: loadingROAS,
    refreshing: refreshingROAS,
    error: roasError,
    refetch: refetchROAS,
  } = useROASDistribution(dateRange);

    // MQL Activity hook
  const {
    data: mqlActivityData,
    loading: loadingMQLActivity,
    refreshing: refreshingMQLActivity,
    error: mqlActivityError,
    refetch: refetchMQLActivity,
  } = useMQLActivity(dateRange);

  // Social Media hook
  const {
    data: socialMediaData,
    loading: loadingSocialMedia,
    refreshing: refreshingSocialMedia,
    error: socialMediaError,
    refetch: refetchSocialMedia,
  } = useSocialMedia(dateRange);

  // AEO hook
  const {
    data: aeoData,
    loading: loadingAEO,
    refreshing: refreshingAEO,
    error: aeoError,
    refetch: refetchAEO,
  } = useAEO(dateRange);

  // Email hook
  const {
    data: emailData,
    loading: loadingEmail,
    refreshing: refreshingEmail,
    error: emailError,
    refetch: refetchEmail,
  } = useEmail(dateRange);

  // LinkedIn Deepdive hook
  const {
    data: linkedinData,
    loading: loadingLinkedIn,
    refreshing: refreshingLinkedIn,
    error: linkedinError,
    refetch: refetchLinkedIn,
  } = useLinkedIn(dateRange);

    // PR hook
  const {
    data: prData,
    loading: loadingPR,
    refreshing: refreshingPR,
    error: prError,
    refetch: refetchPR,
  } = usePRDemo(dateRange); // Changed from usePR to usePRDemo

  // Section collapse state
  const [sectionState, setSectionState] = useState(() => ({
    growthCollapsed: false,
    projectionsCollapsed: false,
    roasCollapsed: false,
    mqlActivityCollapsed: false,
    socialMediaCollapsed: false,
    aeoCollapsed: false,
    emailCollapsed: false,
    linkedinCollapsed: false,
    prCollapsed: false, // ADD THIS

  }));
  

  const updateSectionState = useCallback((key, value) => {
    setSectionState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Set document title and favicon - use shared utility
  useEffect(() => {
    setDocumentMeta(DOCUMENT_TITLES.MARKETING);
  }, []);

  // ============================================================================
  // ============================================================================

  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
  }, []);

  const handleGlobalRefresh = useCallback(async () => {
    setIsRefreshing(true);

    try {
      console.log('🔄 Refreshing marketing dashboard data...');

      // Refetch all data in parallel
      await Promise.all([
        refetchGrowth(),
        refetchROAS(),
        refetchPR(), // ADD THIS

      ]);

      addToast('Marketing data refreshed successfully', 'success');
      console.log('✅ Marketing data refreshed successfully');

    } catch (error) {
      console.error('❌ Refresh failed:', error);
      addToast('Failed to refresh marketing data', 'error');
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchGrowth, refetchROAS, addToast]);

  return (
    <div className="page-wrapper">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="max-w-full mx-auto space-y-6">
        {/* Header Component */}
        <Header
          title="Marketing Performance Dashboard"
          subtitle="Growth equation, projections, and channel ROI analysis"
          lastUpdated={new Date().toISOString()}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onRefresh={handleGlobalRefresh}
          loading={
            isRefreshing ||
            refreshingGrowth ||
            refreshingROAS ||
            refreshingPR
          }
          yearsOnly={true}
        />

        {/* Main Content */}
        <div className="responsive-space">
          {/* Growth Equation Section */}
          <CollapsibleSection
            title="Growth Equation: Know | Like | Trust"
            subtitle="Growth equation and overall conversion metrics"
            collapsed={sectionState.growthCollapsed}
            setCollapsed={(value) =>
              updateSectionState("growthCollapsed", value)
            }
          >
            <GrowthEquation
              data={growthData}
              loading={loadingGrowth}
              error={growthError}
            />
          </CollapsibleSection>

          {/* Projections Section */}
          <CollapsibleSection
            title="Marketing Projections"
            subtitle="Scenario planning and what-if analysis"
            collapsed={sectionState.projectionsCollapsed}
            setCollapsed={(value) =>
              updateSectionState("projectionsCollapsed", value)
            }
          >
            <Projections
              inputs={projectionInputs}
              results={projectionResults}
              updateInput={updateInput}
              resetInputs={resetInputs}
            />
          </CollapsibleSection>

          {/* ROAS Distribution Section */}
          <CollapsibleSection
            title="ROAS & Channel Distribution"
            subtitle="Ad spend allocation and return on investment by channel"
            collapsed={sectionState.roasCollapsed}
            setCollapsed={(value) =>
              updateSectionState("roasCollapsed", value)
            }
          >
            <ROASDistribution
              data={roasData}
              loading={loadingROAS}
              error={roasError}
            />
          </CollapsibleSection>

          {/* MQL Activity Section */}
          <CollapsibleSection
            title="MQL Activity"
            subtitle="Lead source activity and conversion performance"
            collapsed={sectionState.mqlActivityCollapsed}
            setCollapsed={(value) =>
              updateSectionState("mqlActivityCollapsed", value)
            }
          >
            <MQLActivity
              data={mqlActivityData}
              loading={loadingMQLActivity}
              error={mqlActivityError}
            />
          </CollapsibleSection>

          {/* Website Performance Section */}
          <CollapsibleSection
            title="Web Analytics"
            subtitle="Website traffic and organic search performance"
            collapsed={sectionState.sourcePerformanceCollapsed}
            setCollapsed={(value) =>
              updateSectionState("sourcePerformanceCollapsed", value)
            }
          >
            <WebPerformance dateRange={dateRange} />
          </CollapsibleSection>

          {/* PR Section */}
          <CollapsibleSection
            title="Public Relations"
            subtitle="Media placements, reach, and engagement metrics"
            collapsed={sectionState.prCollapsed}
            setCollapsed={(value) =>
              updateSectionState("prCollapsed", value)
            }
          >
            <PR
              data={prData}
              loading={loadingPR}
              error={prError}
            />
          </CollapsibleSection>
        
            {/* Email Section */}
            <CollapsibleSection
                title="Email Marketing/Newsletter"
                subtitle="Email list growth and subscriber metrics"
                collapsed={sectionState.emailCollapsed}
                setCollapsed={(value) =>
                updateSectionState("emailCollapsed", value)
                }
            >
                <Email
                data={emailData}
                loading={loadingEmail}
                error={emailError}
                />
            </CollapsibleSection>

          {/* Social Media Section */}
          <CollapsibleSection
            title="Social Media Performance"
            subtitle="Followers, traffic, and conversions across platforms"
            collapsed={sectionState.socialMediaCollapsed}
            setCollapsed={(value) =>
              updateSectionState("socialMediaCollapsed", value)
            }
          >
            <SocialMedia
              data={socialMediaData}
              loading={loadingSocialMedia}
              error={socialMediaError}
            />
          </CollapsibleSection>

          {/* AEO Section */}
          <CollapsibleSection
            title="AEO (Answer Engine Optimization)"
            subtitle="AI-driven search performance and citations"
            collapsed={sectionState.aeoCollapsed}
            setCollapsed={(value) =>
              updateSectionState("aeoCollapsed", value)
            }
          >
            <AEO
              data={aeoData}
              loading={loadingAEO}
              error={aeoError}
            />
          </CollapsibleSection>

          
          {/* LinkedIn Deepdive Section */}
          <CollapsibleSection
            title="LinkedIn Deepdive"
            subtitle="Professional network growth and Social Selling Index"
            collapsed={sectionState.linkedinCollapsed}
            setCollapsed={(value) =>
              updateSectionState("linkedinCollapsed", value)
            }
          >
            <LinkedInDeepdive
              data={linkedinData}
              loading={loadingLinkedIn}
              error={linkedinError}
            />
          </CollapsibleSection>

        </div>
      </div>
    </div>
  );
};

export default memo(MarketingDashboard);